// AddnewStaff.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import './AddnewStaffpage.css';
import PageAccessPicker from '../../components/PageAccessPicker';
import { FaArrowLeft } from "react-icons/fa6";

// Reads the real reason from any backend error format
const extractErrorMessage = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;

  const parts = [];
  const collect = (value, label) => {
    if (value === null || value === undefined || value === '') return;
    if (typeof value === 'string' || typeof value === 'number') {
      parts.push(label ? `${label}: ${value}` : String(value));
    } else if (Array.isArray(value)) {
      value.forEach((item) => collect(item, label));
    } else if (typeof value === 'object') {
      Object.keys(value).forEach((key) => collect(value[key], key));
    }
  };

  collect(data.error);
  collect(data.detail);
  collect(data.message);
  collect(data.details);
  collect(data.errors);

  if (parts.length === 0) {
    Object.keys(data).forEach((key) => collect(data[key], key));
  }

  return parts.join(' | ');
};


const AddnewStaff = ({ onClose, onAdd, isPage = false, existingStaff = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get shops from location state (passed from StaffMembers)
  const locationState = location.state || {};
  const shopsFromState = locationState.shops || [];
  const selectedShopIdFromState = locationState.selectedShopId || '';
  const selectedShopNameFromState = locationState.selectedShopName || '';

  const [shops, setShops] = useState(shopsFromState);
  const [selectedShopId, setSelectedShopId] = useState(selectedShopIdFromState || '');
  const [selectedShopName, setSelectedShopName] = useState(selectedShopNameFromState || '');
  const [availableCities, setAvailableCities] = useState([]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    userType: 'STAFF',
    houseNumber: '',
    streetNumber: '',
    area: '',
    city: '',
    selectedShopId: selectedShopIdFromState || ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allowedPages, setAllowedPages] = useState([]);

  // Get auth token
  const getAuthToken = () => {
    const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];

    for (let key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.access || parsed.token || parsed.access_token) {
            return parsed.access || parsed.token || parsed.access_token;
          }
          if (parsed.token && typeof parsed.token === 'object') {
            return parsed.token.access || parsed.token.token || parsed.token.access_token;
          }
        } catch (e) {
          return storedData;
        }
      }
    }
    return null;
  };

  // Fetch shops if not passed from state
  useEffect(() => {
    if (shopsFromState.length === 0) {
      fetchShops();
    }
  }, []);

  const fetchShops = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch('https://super-backend.hira-tech.co.uk/user/shops/', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setShops(data);
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
    }
  };

  // Load cities when shop is selected
  useEffect(() => {
    if (selectedShopId) {
      loadCitiesForShop(selectedShopId);
    }
  }, [selectedShopId]);

  const loadCitiesForShop = async (shopId) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      // First check if shop object has cities
      const shop = shops.find(s => s.shop_id.toString() === shopId.toString());

      if (shop && shop.cities && shop.cities.length > 0) {
        setAvailableCities(shop.cities);
        return;
      }

      // If not, fetch from API
      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/shop-cities/?shop_id=${shopId}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCities(data.cities || []);
      }
    } catch (err) {
      console.error('Error loading cities:', err);
      setAvailableCities([]);
    }
  };

  // Handle shop change
  const handleShopChange = (e) => {
    const shopId = e.target.value;
    const shop = shops.find(s => s.shop_id.toString() === shopId.toString());

    setSelectedShopId(shopId);
    setSelectedShopName(shop ? shop.shop_name : '');

    // Reset city when shop changes
    setFormData(prev => ({
      ...prev,
      city: '',
      selectedShopId: shopId
    }));

    // Load cities for new shop
    loadCitiesForShop(shopId);
  };

  // Validate phone number - only check if not empty
  const validatePhone = (phone) => {
    const cleanedPhone = phone.replace(/\s/g, '');
    return cleanedPhone.length > 0;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!selectedShopId) {
      newErrors.selectedShopId = 'Please select a shop';
    }

    if (!formData.houseNumber.trim()) {
      newErrors.houseNumber = 'House number is required';
    }

    if (!formData.streetNumber.trim()) {
      newErrors.streetNumber = 'Street number is required';
    }

    if (!formData.area.trim()) {
      newErrors.area = 'Area is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill all required fields correctly',
        confirmButtonColor: '#004aad',
        confirmButtonText: 'OK'
      });
      return false;
    }
    return true;
  };

  // Navigate to staff members page for the CORRECT selected shop
  const goToStaffMembers = () => {
    console.log('Navigating to staff members page...');
    if (selectedShopId) {
      navigate(`/shop/${selectedShopId}/staff`);
    } else {
      navigate('/staff');
    }
  };

  const showSuccessAlert = () => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: 'Staff member created successfully!',
      confirmButtonColor: '#004aad',
      confirmButtonText: 'OK',
      timer: 2000,
      timerProgressBar: true
    }).then(() => {
      goToStaffMembers();
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message || 'Failed to create staff member. Please try again.',
      confirmButtonColor: '#004aad',
      confirmButtonText: 'OK'
    });
  };

  const showWarningAlert = async (message) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: message || 'Do you want to cancel? Your data will be lost.',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'No, stay'
    });
    return result.isConfirmed;
  };

  const showLoadingAlert = () => {
    Swal.fire({
      title: 'Creating Staff...',
      text: 'Please wait while we create the staff member',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  };

  const closeLoadingAlert = () => {
    Swal.close();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm() && !isSubmitting) {
      setIsSubmitting(true);
      showLoadingAlert();

      try {
        const token = getAuthToken();

        if (!token) {
          closeLoadingAlert();
          showErrorAlert('No authentication token found. Please login again.');
          setTimeout(() => {
            navigate('/login');
          }, 1000);
          return;
        }

        // Prepare data for API
        const apiData = {
          first_Name: formData.firstName,
          last_Name: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone_Number: formData.phone,
          type: formData.userType.toUpperCase(),
          shop_id: selectedShopId,
          address_details: {
            house_Number: formData.houseNumber,
            street_Number: formData.streetNumber,
            area: formData.area,
            city: formData.city,
            full_address: `${formData.houseNumber}, ${formData.streetNumber}, ${formData.area}, ${formData.city}`
          }
        };

        console.log('Sending data to API:', apiData);

        const response = await fetch('https://super-backend.hira-tech.co.uk/user/registerStaff/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData)
        });

        closeLoadingAlert();

        if (response.ok) {
          const data = await response.json();
          console.log('Staff created successfully:', data);

          // Save the pages this STAFF member can open
          if (['STAFF', 'ADMIN'].includes(formData.userType.toUpperCase()) && data && data.id) {
            try {
              await fetch(`https://super-backend.hira-tech.co.uk/user/${data.id}/permissions/update/`, {
                method: 'PATCH',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ allowed_pages: allowedPages })
              });
            } catch (permError) {
              console.error('Could not save page access:', permError);
            }
          }

          showSuccessAlert();

          if (onAdd) {
            onAdd(formData);
          }
        } else {
          let errorMessage = 'Failed to create staff member';
          try {
            const errorData = await response.json();
            errorMessage = extractErrorMessage(errorData) || errorMessage;
          } catch (e) {
            console.error('Error parsing error response:', e);
          }
          showErrorAlert(errorMessage);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error creating staff:', error);
        closeLoadingAlert();
        showErrorAlert('Network error. Please check your connection and try again.');
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle close button click - navigate to staff members
  const handleClose = async () => {
    const confirmed = await showWarningAlert('Are you sure you want to cancel? Any unsaved data will be lost.');
    if (confirmed) {
      console.log('Close button clicked, navigating to staff members...');
      goToStaffMembers();
    }
  };

  // Handle cancel button click - navigate to staff members
  const handleCancel = async () => {
    const confirmed = await showWarningAlert('Are you sure you want to cancel? Any unsaved data will be lost.');
    if (confirmed) {
      console.log('Cancel button clicked, navigating to staff members...');
      goToStaffMembers();
    }
  };

  return (
    <div className="staff-form page-form">
      <div className="form-header">
        {/* Heading Text */}
        <h2>Create New Staff User Account</h2>

        {/* Cross (×) button ki jagah Back button lagayein */}
        <button className="back-button-top" onClick={handleClose}>
          ← Back
        </button>
      </div>


      <form onSubmit={handleSubmit}>
        {/* ✅ ROW 1: Shop, First Name, Last Name (Modified Layout) */}
        <div className="form-row">
          <div className="form-group">
            <label>Select Shop </label>
            <select
              name="selectedShopId"
              value={selectedShopId}
              onChange={handleShopChange}
              className={errors.selectedShopId ? 'error' : ''}
            >
              <option value="">-- Select a Shop --</option>
              {shops.map(shop => (
                <option key={shop.shop_id} value={shop.shop_id.toString()}>
                  {shop.shop_name}
                </option>
              ))}
            </select>
            {errors.selectedShopId && <span className="error-message">{errors.selectedShopId}</span>}

          </div>

          <div className="form-group">
            <label>First Name </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter First Name"
              className={errors.firstName ? 'error' : ''}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label>Last Name </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter Last Name"
              className={errors.lastName ? 'error' : ''}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>

        {/* ✅ ROW 2: User Type, Email, Password (Modified Layout) */}
        <div className="form-row">
          <div className="form-group">
            <label>User Type </label>
            <select
              name="userType"
              value={formData.userType}
              onChange={handleChange}
            >
              <option value="STAFF">STAFF</option>
            </select>
          </div>

          <div className="form-group">
            <label>Email </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter Email Address"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>Password </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter Password (min 6 characters)"
              className={errors.password ? 'error' : ''}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>
        </div>

        {/* ✅ ROW 3: Phone Number, City, House Number */}
        <div className="form-row">
          <div className="form-group">
            <label>Phone Number </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter Phone number"
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label>City </label>
            <select
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={errors.city ? 'error' : ''}
              disabled={!selectedShopId || availableCities.length === 0}
            >
              <option value="">
                {selectedShopId
                  ? (availableCities.length > 0 ? '-- Select a City --' : 'No cities available')
                  : '-- Select a Shop First --'}
              </option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            {errors.city && <span className="error-message">{errors.city}</span>}
            {!selectedShopId && <small className="helper-text">Select a shop to see available cities</small>}
          </div>

          <div className="form-group">
            <label>House Number </label>
            <input
              type="text"
              name="houseNumber"
              value={formData.houseNumber}
              onChange={handleChange}
              placeholder="Enter House Number"
              className={errors.houseNumber ? 'error' : ''}
            />
            {errors.houseNumber && <span className="error-message">{errors.houseNumber}</span>}
          </div>
        </div>

        {/* ✅ ROW 4: Street Number, Area, Postal Code */}
        <div className="form-row">
          <div className="form-group">
            <label>Street Number </label>
            <input
              type="text"
              name="streetNumber"
              value={formData.streetNumber}
              onChange={handleChange}
              placeholder="Enter Street Number"
              className={errors.streetNumber ? 'error' : ''}
            />
            {errors.streetNumber && <span className="error-message">{errors.streetNumber}</span>}
          </div>

          <div className="form-group">
            <label>Area </label>
            <input
              type="text"
              name="area"
              value={formData.area}
              onChange={handleChange}
              placeholder="Enter Area/Phase"
              className={errors.area ? 'error' : ''}
            />
            {errors.area && <span className="error-message">{errors.area}</span>}
          </div>

          <div className="form-group">
            <label>Postal Code (Optional)</label>
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode || ''}
              onChange={handleChange}
              placeholder="Enter Postal Code"
            />
          </div>
        </div>

        {['STAFF', 'ADMIN'].includes(formData.userType.toUpperCase()) && (
          <PageAccessPicker value={allowedPages} onChange={setAllowedPages} />
        )}

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={handleCancel}>
            Cancel
          </button>
          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Staff'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddnewStaff;